import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import twilioClient from '../../utils/twilioClient';

const { FiCheck, FiX, FiRefreshCw, FiAlertCircle, FiSettings, FiInfo } = FiIcons;

const TwilioSettings = () => {
  const [status, setStatus] = useState({ loading: true, data: null, error: null });
  const [testing, setTesting] = useState(false);
  const [smsForwardTo, setSmsForwardTo] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    testConnection();
    loadSettings();
  }, []);

  const loadSettings = () => {
    // In a real app, this would load from a settings API
    // For now, we'll just show the environment variable status
    const forwardTo = process.env.TWILIO_SMS_FORWARD_TO || '';
    setSmsForwardTo(forwardTo);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      // In a real app, this would save to a settings API
      // For now, we'll just show a success message
      alert('Settings saved! (Note: In production, this would update environment variables)');
      setSavingSettings(false);
    } catch (error) {
      alert('Failed to save settings: ' + error.message);
      setSavingSettings(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const statusData = await twilioClient.getStatus();
      setStatus({ loading: false, data: statusData, error: null });
    } catch (error) {
      setStatus({ loading: false, data: null, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  const requiredEnvVars = [
    { name: 'TWILIO_ACCOUNT_SID', description: 'Your Twilio Account SID', required: true },
    { name: 'TWILIO_AUTH_TOKEN', description: 'Your Twilio Auth Token', required: true },
    { name: 'TWILIO_DEFAULT_FROM_NUMBER', description: 'Default phone number for outbound messages/calls (E.164 format)', required: true },
    { name: 'TWILIO_API_KEY', description: 'API Key for enhanced security (optional)', required: false },
    { name: 'TWILIO_API_SECRET', description: 'API Secret for enhanced security (optional)', required: false }
  ];

  return (
    <div className="space-y-8">
      {/* Connection Status */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold flex items-center">
            <SafeIcon icon={FiSettings} className="mr-2" />
            Twilio Connection Status
          </h2>
          <button onClick={testConnection} disabled={testing} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            <SafeIcon icon={testing ? FiRefreshCw : FiRefreshCw} className={`mr-2 ${testing ? 'animate-spin' : ''}`} />
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>
        {status.loading ? (
          <div className="flex items-center text-gray-500">
            <SafeIcon icon={FiRefreshCw} className="animate-spin mr-2" />
            Checking connection...
          </div>
        ) : status.error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center text-red-800 mb-2">
              <SafeIcon icon={FiX} className="mr-2" />
              <strong>Connection Failed</strong>
            </div>
            <p className="text-red-700 mb-4">{status.error}</p>
            <div className="text-sm text-red-600">
              <p>Please check that your environment variables are properly configured.</p>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center text-green-800 mb-2">
              <SafeIcon icon={FiCheck} className="mr-2" />
              <strong>Connection Successful</strong>
            </div>
            <div className="space-y-1 text-green-700">
              <div><strong>Account:</strong> {status.data.friendlyName}</div>
              <div><strong>Account SID:</strong> {status.data.accountSid}</div>
              <div><strong>Status:</strong> {status.data.status}</div>
            </div>
          </div>
        )}
      </div>

      {/* Optional Settings */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-6">Optional Settings</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Forward incoming SMS to
              <span className="ml-2 relative group">
                <SafeIcon icon={FiInfo} className="text-gray-400 cursor-help" />
                <div className="absolute left-0 top-full mt-1 w-80 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  If set, all incoming SMS messages will be forwarded to this number. Must be in E.164 format (+61..., +1..., etc.).
                </div>
              </span>
            </label>
            <input
              type="tel"
              value={smsForwardTo}
              onChange={(e) => setSmsForwardTo(e.target.value)}
              placeholder="+61412345678"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter a phone number in E.164 format to forward all incoming SMS messages
            </p>
          </div>

          {/* SMS Forwarding Status */}
          <div className="mt-4">
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-700 mr-2">SMS Forwarding Status:</span>
              {smsForwardTo ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Forwarding to {smsForwardTo}
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Forwarding OFF
                </span>
              )}
            </div>
          </div>

          <div className="pt-4 border-t">
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Note: In production, SMS forwarding requires updating the TWILIO_SMS_FORWARD_TO environment variable
            </p>
          </div>
        </div>
      </div>

      {/* Environment Variables */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-6">Required Environment Variables</h2>
        <div className="space-y-4">
          {requiredEnvVars.map((envVar) => (
            <div key={envVar.name} className="border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                      {envVar.name}
                    </code>
                    {envVar.required && (
                      <span className="ml-2 text-red-500 text-sm">*required</span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mt-1">{envVar.description}</p>
                </div>
              </div>
            </div>
          ))}
          
          {/* SMS Forwarding Environment Variable */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                    TWILIO_SMS_FORWARD_TO
                  </code>
                  <span className="ml-2 text-blue-500 text-sm">*optional</span>
                </div>
                <p className="text-gray-600 text-sm mt-1">Phone number to forward incoming SMS messages to (E.164 format)</p>
              </div>
            </div>
          </div>
        </div>
        {status.error && (
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center text-yellow-800 mb-2">
              <SafeIcon icon={FiAlertCircle} className="mr-2" />
              <strong>Configuration Help</strong>
            </div>
            <div className="text-yellow-700 text-sm space-y-2">
              <p>If you're seeing connection errors, please ensure:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>All required environment variables are set in your hosting platform</li>
                <li>Your Twilio Account SID and Auth Token are correct</li>
                <li>Your Twilio account is active and in good standing</li>
                <li>The default phone number is a valid Twilio number in E.164 format</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Webhook Configuration */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-6">Webhook Configuration</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">SMS Webhook URL</h3>
            <div className="bg-gray-100 p-3 rounded border font-mono text-sm">
              {window.location.origin}/api/twilio/webhooks/incoming-sms
            </div>
            <p className="text-gray-600 text-sm mt-1">
              Configure this URL in your Twilio phone number settings for SMS webhooks
            </p>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Voice Webhook URL</h3>
            <div className="bg-gray-100 p-3 rounded border font-mono text-sm">
              {window.location.origin}/api/twilio/webhooks/incoming-call
            </div>
            <p className="text-gray-600 text-sm mt-1">
              Configure this URL in your Twilio phone number settings for voice webhooks
            </p>
          </div>
        </div>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
          <p className="text-blue-800 text-sm">
            <strong>Note:</strong> Make sure your webhook URLs are publicly accessible and use HTTPS in production. 
            You can configure these URLs in the Twilio Console under Phone Numbers → Manage → Active numbers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwilioSettings;